import {
  CrmOpportunityForInput,
  IOpportunityQuote,
  IOpportunityWithQuotes,
} from '@monetize/types/app';
import { useState } from 'react';
import { FiExternalLink } from 'react-icons/fi';
import { MdCheck, MdClose } from 'react-icons/md';
import { handleApiErrorToast } from '../../api/axios';
import {
  doEvaluateQuoteRules,
  useLinkOpportunityToCrm,
} from '../../api/cpqService';
import { enqueueForQuote } from '../../api/quoteWriteChain';
import { OpportunitySearchInput } from '../../routes/Quotes/Quote/components/NewQuoteForm/OpportunitySearchInput';
import { EXTERNAL_SYSTEM_ICONS } from '../ExternalAssociations/externalSystemIcons';
import {
  MButton,
  MCustomIconButton,
  MFlex,
  MIcon,
  MLink,
  MText,
} from '../Monetize';
import { CrmOpportunityLink } from './useCrmOpportunityLink';

interface OpportunityModalProps {
  opportunity: IOpportunityWithQuotes;
  crmLink?: CrmOpportunityLink;
  /** Disables linking to a Salesforce opportunity; an existing link still shows. */
  isDisabled?: boolean;
  quotes: IOpportunityQuote[];
}

export const OpportunityLinkCrmOpportunityInput = ({
  opportunity,
  crmLink,
  isDisabled,
  quotes,
}: OpportunityModalProps) => {
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [crmOpportunity, setCrmOpportunity] =
    useState<CrmOpportunityForInput | null>(null);
  const { mutateAsync: doLinkOpportunityToCrm, isPending: isLoading } =
    useLinkOpportunityToCrm();
  const CrmIcon = crmLink && EXTERNAL_SYSTEM_ICONS[crmLink.system];

  const handleSubmit = async () => {
    try {
      if (crmOpportunity?.id) {
        await doLinkOpportunityToCrm({
          opportunityId: opportunity.id,
          customId: crmOpportunity.id,
        });

        // One of these may be the quote open in the editor, so each evaluation
        // takes its quote's chain; different quotes still run in parallel.
        await Promise.all(
          quotes.map((quote) =>
            enqueueForQuote(quote.id, () => doEvaluateQuoteRules(quote.id)),
          ),
        );
      }
      setIsSearchActive(false);
      setCrmOpportunity(null);
    } catch (err) {
      handleApiErrorToast(err);
    }
  };

  function handleCancel() {
    setIsSearchActive(false);
    setCrmOpportunity(null);
  }

  return (
    <>
      {!isDisabled && !crmLink && !opportunity.customId && !isSearchActive && (
        <MButton
          variant="tertiary"
          ml={-3}
          onClick={() => setIsSearchActive(true)}
        >
          Link to Salesforce
        </MButton>
      )}
      {!isDisabled && isSearchActive && (
        <MFlex>
          <OpportunitySearchInput
            label=""
            accountId={opportunity.accountId}
            crmOpportunityId={crmOpportunity?.id}
            onChange={setCrmOpportunity}
          />
          <MCustomIconButton
            variant="icon"
            btnSize={4}
            mt={2}
            ml={2}
            icon={MdClose}
            aria-label="Cancel"
            onClick={handleCancel}
            isDisabled={isLoading}
          />
          <MCustomIconButton
            variant="icon"
            btnSize={4}
            mt={2}
            ml={2}
            iconColor="tIndigo.base"
            icon={MdCheck}
            aria-label="Confirm"
            isLoading={isLoading}
            isDisabled={!crmOpportunity?.id || isLoading}
            onClick={handleSubmit}
          />
        </MFlex>
      )}
      {crmLink && (
        <MFlex columnGap={1} alignItems="center">
          {CrmIcon && <CrmIcon />}
          {/* FIXME: The CRM opportunity might have a different name - we don't store that data */}
          {crmLink.url ? (
            <MLink
              href={crmLink.url}
              target="_blank"
              rel="noopener noreferrer"
              fontWeight="bold"
              color="tIndigo.base"
              lineClamp={1}
            >
              {opportunity.name}
              <MIcon as={FiExternalLink} ml="1" color="tIndigo.base" />
            </MLink>
          ) : (
            <MText lineClamp={1}>{crmLink.id}</MText>
          )}
        </MFlex>
      )}
    </>
  );
};
